import {
  Box,
  BoxSx,
  Grid,
  GridColumn,
  Link,
  List,
  ListItem,
  Text,
  ThemeOverride,
  ThemeProvider
} from "@modernatx/ui-kit-react";
import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";
import React from "react";
import { Descendant } from "slate";
import { RenderElementProps, RenderLeafProps } from "slate-react";
import { Global } from "theme-ui";

import { RenderMetadata } from "@/components/RenderMetadata";
import { RenderContent } from "@/components/rsv/RenderContent";
import { useExperience } from "@/context/ExperienceContext";
import { ExitModalProvider } from "@/hooks/useExitModal";
import { BlockPageProps } from "@/types/Block";

import { ContentPage } from "./ContentPage";
import { PrescribingInfoModal } from "./PrescribingInfoModal";

const TextBlockPopoverV1 = dynamic(
  () =>
    import("@/components/TextBlockPopover-V1/TextBlockPopover").then((res) => res.TextBlockPopover),
  {
    ssr: false
  }
);

const PageControls = dynamic(() => import("./PageControls").then((res) => res.PageControls), {
  ssr: false
});

interface PageV1PropsWithExperience {
  content: BlockPageProps | null;
  textBlocks: Record<string, Descendant[]> | null;
}

export const PageV1: React.FC<PageV1PropsWithExperience> = ({ content, textBlocks }) => {
  const { environment, country, screenshot } = useExperience();

  const background = (
    <Global
      styles={{
        html: {
          backgroundColor: "white"
        }
      }}
    />
  );
  const searchParams = useSearchParams();
  const editing = !!searchParams.get("edit") && environment === "local";
  const renderElement = React.useCallback((props: RenderElementProps) => {
    const { attributes, children, element: elementInitial } = props;
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      children: _childrenOmitted,
      fontSize,
      hangingIndent,
      start,
      type,
      ...element
    } = elementInitial;
    const styles: BoxSx = {
      display: type === "span" ? "block" : null,
      fontSize,
      lineHeight: fontSize || "inherit",
      ...(hangingIndent
        ? {
            paddingInlineStart: () => "0.7em",
            textIndent: "-0.7em"
          }
        : {}),
      ...element
    };

    switch (type) {
      case "li-text":
        return (
          <Box as="div" {...attributes} sx={styles}>
            {children}
          </Box>
        );
      case "li":
        return (
          <ListItem {...attributes} sx={styles}>
            {children}
          </ListItem>
        );
      case "ol":
        return (
          <List {...attributes} start={start} sx={styles} variant="ordered">
            {children}
          </List>
        );
      case "ul":
        return (
          <List {...attributes} sx={styles} variant="unordered">
            {children}
          </List>
        );
      default:
        return (
          <Box as={type as React.ComponentProps<typeof Text>["as"]} {...attributes} sx={styles}>
            {children}
          </Box>
        );
    }
  }, []);

  const renderLeaf = React.useCallback(
    (props: RenderLeafProps) => {
      const { leaf: leafInitial } = props;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { fontSize, type, text: _omittedText, verticalAlign, ...leaf } = leafInitial;
      const styles: BoxSx = {
        lineHeight: fontSize,
        ...leaf,
        fontSize,
        verticalAlign
      };

      if (!editing && !("whitespace" in styles)) {
        styles.whiteSpace = "pre-line";
      }

      if (type === "sup") {
        return (
          <Box as="span" sx={styles} {...props.attributes}>
            <Box as="sup" sx={{ fontSize: () => "55%", verticalAlign: "revert" }}>
              {props.children}
            </Box>
          </Box>
        );
      }

      if (leaf.href) {
        return (
          <Link href={editing ? undefined : leaf.href} sx={styles} {...props.attributes}>
            {props.children}
          </Link>
        );
      }

      return (
        <Box as={type || "span"} sx={styles} {...props.attributes}>
          {props.children}
        </Box>
      );
    },
    [editing]
  );

  if (!country || !content || typeof content.theme !== "object") {
    return (
      <ThemeProvider usingStyleInheritance={true}>
        {background}
        <Grid>
          <GridColumn
            sx={{
              alignItems: "center",
              display: "flex",
              height: "100vh",
              justifyContent: "center"
            }}
          >
            <Text size="lg" as="p">
              {!country || !content
                ? "The site is not available in your country."
                : "A theme object is required"}
            </Text>
          </GridColumn>
        </Grid>
      </ThemeProvider>
    );
  }

  return (
    <ContentPage
      renderElement={renderElement}
      renderLeaf={renderLeaf}
      renderTextBlockPopover={() => <TextBlockPopoverV1 />}
      textBlocks={textBlocks}
    >
      <ThemeProvider expandContent={screenshot} theme={content.theme} usingStyleInheritance={true}>
        <ThemeOverride mode={content.themeMode}>
          <Global
            styles={{
              html: {
                backgroundColor: content.backgroundColor as string,
                wordBreak: content.wordBreak
              }
            }}
          />
        </ThemeOverride>
        <ExitModalProvider exitModal={content.exitModal}>
          <PrescribingInfoModal />
          <RenderMetadata {...content.metadata} renderGoogleRecaptcha={true} />
          <RenderContent content={content} />
        </ExitModalProvider>
      </ThemeProvider>
      {(environment === "local" || environment === "preview") && <PageControls />}
    </ContentPage>
  );
};
